import { httpsCallable } from 'firebase/functions';
import { functions } from './firebaseFunctions';

/**
 * Interface for the request to revoke an attachment share link
 */
export interface RevokeAttachmentLinkRequest {
    /** ID of the attachment to revoke sharing for */
    fileId: string;
    /** ID of the version to manage */
    versionId: string;
    /** Path to the artifact (form or entity) that references the attachment */
    artifactPath?: string;
}

/**
 * Result of a share link revocation operation
 */
export interface RevokeAttachmentLinkResult {
    /** Whether the operation was successful */
    success: boolean;
    /** ID of the file that was modified */
    fileId: string;
    /** ID of the version that was modified */
    versionId: string;
}

/**
 * Revokes a shareable link for an attachment version
 * 
 * This function calls the backend cloud function to revoke access to a previously 
 * shared attachment. It updates the storage metadata and Firestore records to 
 * indicate the file is no longer shared.
 * 
 * @param fileId - ID of the attachment to revoke sharing for
 * @param versionId - ID of the version to manage
 * @param artifactPath - Optional path to the artifact (form/entity) referencing the attachment
 * @returns Promise resolving to the revocation result
 */
export const revokeAttachmentLink = async (
    fileId: string, 
    versionId: string,
    artifactPath?: string
): Promise<RevokeAttachmentLinkResult> => {
    const revokeAttachmentLinkFn = httpsCallable<RevokeAttachmentLinkRequest, RevokeAttachmentLinkResult>(
        functions, 
        'revokeAttachmentLink'
    );
    
    const response = await revokeAttachmentLinkFn({
        fileId,
        versionId,
        artifactPath
    });
    
    return response.data;
}; 